/**
 * Colligative properties of dilute solutions:
 *
 *   boiling-point elevation    ΔTb = i·Kb·m
 *   freezing-point depression  ΔTf = i·Kf·m
 *   osmotic pressure           Π  = i·M·R·T
 *
 * Solvent constants are OpenStax Chemistry 2e, Table 11.2 (boiling and freezing points
 * at 1 atm; Kb and Kf in °C·kg/mol). The van 't Hoff factors are Table 11.3 (predicted
 * and measured for 0.050 m aqueous solutions). R = 8.314 462 618 J/(mol·K) is exact, so
 * Π comes out in pascals from mol/m³ and is converted to the unit the reader chose.
 */

import type { UnitDef } from "@/lib/tool-kit/chemistry/units";
import { GAS_CONSTANT } from "./constants";
import {
  HEAT_MASS_UNITS,
  type HeatFailure,
  type ReadSpec,
  readAll,
  unitFactor,
} from "./heat";
import { fromKelvin, PRESSURE_UNITS, toKelvin } from "./units";

export interface Solvent {
  id: string;
  /** Normal boiling point, °C. */
  boil: number;
  /** °C·kg/mol */
  kb: number;
  /** Normal freezing point, °C. */
  freeze: number;
  /** °C·kg/mol */
  kf: number;
}

export const SOLVENTS: readonly Solvent[] = [
  { id: "water", boil: 100.0, kb: 0.512, freeze: 0.0, kf: 1.86 },
  { id: "aceticAcid", boil: 118.1, kb: 3.07, freeze: 16.6, kf: 3.9 },
  { id: "benzene", boil: 80.1, kb: 2.53, freeze: 5.5, kf: 5.12 },
  { id: "chloroform", boil: 61.26, kb: 3.63, freeze: -63.5, kf: 4.68 },
  { id: "nitrobenzene", boil: 210.9, kb: 5.24, freeze: 5.67, kf: 8.1 },
];

export interface Solute {
  id: string;
  /** Formula or class, written as chemists write it. */
  formula: string;
  /** Predicted (complete dissociation). */
  ideal: number;
  /** Measured in 0.050 m aqueous solution. */
  measured: number;
}

export const SOLUTES: readonly Solute[] = [
  { id: "sucrose", formula: "C₁₂H₂₂O₁₁", ideal: 1, measured: 1.0 },
  { id: "nacl", formula: "NaCl", ideal: 2, measured: 1.9 },
  { id: "hcl", formula: "HCl", ideal: 2, measured: 1.9 },
  { id: "mgso4", formula: "MgSO₄", ideal: 2, measured: 1.3 },
  { id: "mgcl2", formula: "MgCl₂", ideal: 3, measured: 2.7 },
  { id: "fecl3", formula: "FeCl₃", ideal: 4, measured: 3.4 },
];

export function findSolvent(id: string): Solvent | undefined {
  return SOLVENTS.find((solvent) => solvent.id === id);
}

export type ColligativeFailure =
  | HeatFailure
  | { ok: false; reason: "wrongDirection" };

export type Property = "boil" | "freeze";

const MOLALITY_KIND = "positive" as const;

/** Reads the van 't Hoff factor: any positive number (association can push it below 1). */
function iSpec(text: string): ReadSpec {
  return { key: "i", text, kind: MOLALITY_KIND, unit: "", factor: 1 };
}

function massSpec(key: string, text: string, unit: string): ReadSpec {
  return {
    key,
    text,
    kind: "positive",
    unit: "",
    factor: unitFactor(HEAT_MASS_UNITS, unit),
  };
}

/** The solvent constant and pure-solvent point for the chosen solvent or a custom one. */
function solventSpecs(
  property: Property,
  solvent: string,
  text: { k: string; point: string },
  tempUnit: string
): { specs: ReadSpec[]; k?: number; pointC?: number } {
  const known = findSolvent(solvent);
  if (known) {
    return {
      specs: [],
      k: property === "boil" ? known.kb : known.kf,
      pointC: property === "boil" ? known.boil : known.freeze,
    };
  }
  return {
    specs: [
      { key: "k", text: text.k, kind: "positive", unit: "", factor: 1 },
      {
        key: "point",
        text: text.point,
        kind: "temperature",
        unit: tempUnit,
        factor: 1,
      },
    ],
  };
}

function resolveSolvent(
  base: { k?: number; pointC?: number },
  values: Record<string, number>
): { k: number; pointC: number } {
  return {
    k: base.k ?? values.k,
    pointC: base.pointC ?? fromKelvin(values.point, "C"),
  };
}

/* ------------------------------------------- boiling / freezing point */

export type ConcentrationMethod = "molality" | "mass";

export interface FixedPointInput {
  property: Property;
  solvent: string;
  method: ConcentrationMethod;
  text: {
    k: string;
    point: string;
    molality: string;
    soluteMass: string;
    molarMass: string;
    solventMass: string;
    i: string;
  };
  unit: { t: string; soluteMass: string; solventMass: string };
}

export interface FixedPointSolution {
  ok: true;
  property: Property;
  /** °C·kg/mol */
  k: number;
  i: number;
  /** mol/kg */
  molality: number;
  /** Moles of solute, and the masses it was worked out from (g, g/mol); null for a typed molality. */
  moles: number | null;
  soluteGrams: number | null;
  solventGrams: number | null;
  molarMass: number | null;
  /** The size of the shift, in °C (= K). */
  delta: number;
  /** Pure-solvent and solution points, °C. */
  pureC: number;
  solutionC: number;
  /** Solution point in the chosen unit. */
  solutionShown: number;
}

export type FixedPointOutcome = FixedPointSolution | ColligativeFailure;

export function solveFixedPoint(input: FixedPointInput): FixedPointOutcome {
  const { property, unit } = input;
  const solvent = solventSpecs(property, input.solvent, input.text, unit.t);
  const specs: ReadSpec[] = [...solvent.specs, iSpec(input.text.i)];
  if (input.method === "molality") {
    specs.push({
      key: "molality",
      text: input.text.molality,
      kind: "positive",
      unit: "",
      factor: 1,
    });
  } else {
    specs.push(
      massSpec("soluteMass", input.text.soluteMass, unit.soluteMass),
      {
        key: "molarMass",
        text: input.text.molarMass,
        kind: "positive",
        unit: "",
        factor: 1,
      },
      massSpec("solventMass", input.text.solventMass, unit.solventMass)
    );
  }
  const read = readAll(specs);
  if ("ok" in read) {
    return read;
  }
  const v = read.values;
  const { k, pointC } = resolveSolvent(solvent, v);
  const moles = input.method === "mass" ? v.soluteMass / v.molarMass : null;
  const molality = moles === null ? v.molality : moles / (v.solventMass / 1000);
  const delta = v.i * k * molality;
  const solutionC = property === "boil" ? pointC + delta : pointC - delta;
  if (!(toKelvin(solutionC, "C") > 0)) {
    return { ok: false, reason: "resultBelowZero" };
  }
  return {
    ok: true,
    property,
    k,
    i: v.i,
    molality,
    moles,
    soluteGrams: input.method === "mass" ? v.soluteMass : null,
    solventGrams: input.method === "mass" ? v.solventMass : null,
    molarMass: input.method === "mass" ? v.molarMass : null,
    delta,
    pureC: pointC,
    solutionC,
    solutionShown: fromKelvin(toKelvin(solutionC, "C"), unit.t),
  };
}

/* --------------------------------------------------- molar mass from ΔT */

export interface MolarMassInput {
  property: Property;
  solvent: string;
  text: {
    k: string;
    point: string;
    observed: string;
    soluteMass: string;
    solventMass: string;
    i: string;
  };
  unit: { t: string; soluteMass: string; solventMass: string };
}

export interface MolarMassSolution {
  ok: true;
  property: Property;
  k: number;
  i: number;
  pureC: number;
  observedC: number;
  delta: number;
  molality: number;
  moles: number;
  /** Masses as read, in grams. */
  soluteGrams: number;
  solventGrams: number;
  /** g/mol */
  molarMass: number;
}

export type MolarMassOutcome = MolarMassSolution | ColligativeFailure;

export function solveMolarMass(input: MolarMassInput): MolarMassOutcome {
  const { property, unit } = input;
  const solvent = solventSpecs(property, input.solvent, input.text, unit.t);
  const read = readAll([
    ...solvent.specs,
    iSpec(input.text.i),
    {
      key: "observed",
      text: input.text.observed,
      kind: "temperature",
      unit: unit.t,
      factor: 1,
    },
    massSpec("soluteMass", input.text.soluteMass, unit.soluteMass),
    massSpec("solventMass", input.text.solventMass, unit.solventMass),
  ]);
  if ("ok" in read) {
    return read;
  }
  const v = read.values;
  const { k, pointC } = resolveSolvent(solvent, v);
  const observedC = fromKelvin(v.observed, "C");
  const delta = property === "boil" ? observedC - pointC : pointC - observedC;
  if (!(delta > 0)) {
    return { ok: false, reason: "wrongDirection" };
  }
  const molality = delta / (v.i * k);
  const moles = molality * (v.solventMass / 1000);
  return {
    ok: true,
    property,
    k,
    i: v.i,
    pureC: pointC,
    observedC,
    delta,
    molality,
    moles,
    soluteGrams: v.soluteMass,
    solventGrams: v.solventMass,
    molarMass: v.soluteMass / moles,
  };
}

/* ------------------------------------------------- osmotic pressure */

export type OsmoticTerm = "pi" | "m" | "mm";

export const OSMOTIC_CONCENTRATION_UNITS: readonly UnitDef[] = [
  { id: "mM", symbol: "mM", factor: 1e-3 },
  { id: "M", symbol: "M", factor: 1 },
];

export const OSMOTIC_VOLUME_UNITS: readonly UnitDef[] = [
  { id: "mL", symbol: "mL", factor: 1e-3 },
  { id: "L", symbol: "L", factor: 1 },
];

export interface OsmoticInput {
  unknown: OsmoticTerm;
  text: {
    pi: string;
    m: string;
    t: string;
    i: string;
    mass: string;
    vol: string;
  };
  unit: { pi: string; m: string; t: string; mass: string; vol: string };
}

export interface OsmoticSolution {
  ok: true;
  unknown: OsmoticTerm;
  i: number;
  kelvin: number;
  /** Pa, mol/L of solute (formula units), and the molar mass in g/mol when known. */
  pi: number;
  m: number;
  molarMass: number | null;
  /** For the molar-mass solve: the sample's mass in g, its volume in L and its moles. */
  massGrams: number | null;
  volumeLitres: number | null;
  moles: number | null;
  answer: number;
  answerUnit: string;
}

export type OsmoticOutcome = OsmoticSolution | ColligativeFailure;

function pascalsOf(unit: string): number {
  return PRESSURE_UNITS.find((u) => u.id === unit)?.pascals ?? 1;
}

function osmoticSpecs(input: OsmoticInput): ReadSpec[] {
  const { unknown, unit } = input;
  const specs: ReadSpec[] = [
    {
      key: "t",
      text: input.text.t,
      kind: "temperature",
      unit: unit.t,
      factor: 1,
    },
    iSpec(input.text.i),
  ];
  if (unknown === "pi") {
    specs.push({
      key: "m",
      text: input.text.m,
      kind: "positive",
      unit: "",
      factor: unitFactor(OSMOTIC_CONCENTRATION_UNITS, unit.m),
    });
    return specs;
  }
  specs.push({
    key: "pi",
    text: input.text.pi,
    kind: "positive",
    unit: "",
    factor: pascalsOf(unit.pi),
  });
  if (unknown === "mm") {
    specs.push(massSpec("mass", input.text.mass, unit.mass), {
      key: "vol",
      text: input.text.vol,
      kind: "positive",
      unit: "",
      factor: unitFactor(OSMOTIC_VOLUME_UNITS, unit.vol),
    });
  }
  return specs;
}

const OSMOTIC_ANSWER_UNITS = { mm: "gmol" } as const;

export function solveOsmotic(input: OsmoticInput): OsmoticOutcome {
  const { unknown, unit } = input;
  const read = readAll(osmoticSpecs(input));
  if ("ok" in read) {
    return read;
  }
  const v = read.values;
  const rt = GAS_CONSTANT * v.t * 1000;
  const piPa = unknown === "pi" ? v.i * v.m * rt : v.pi;
  const molar = unknown === "pi" ? v.m : piPa / (v.i * rt);
  const molarMass = unknown === "mm" ? v.mass / (molar * v.vol) : null;
  const answers = {
    pi: piPa / pascalsOf(unit.pi),
    m: molar / unitFactor(OSMOTIC_CONCENTRATION_UNITS, unit.m),
    mm: molarMass ?? 0,
  };
  const answerUnits = { pi: unit.pi, m: unit.m, mm: OSMOTIC_ANSWER_UNITS.mm };
  return {
    ok: true,
    unknown,
    i: v.i,
    kelvin: v.t,
    pi: piPa,
    m: molar,
    molarMass,
    massGrams: unknown === "mm" ? v.mass : null,
    volumeLitres: unknown === "mm" ? v.vol : null,
    moles: unknown === "mm" ? molar * v.vol : null,
    answer: answers[unknown],
    answerUnit: answerUnits[unknown],
  };
}
